import { MouseEvent, useMemo, useState } from "react";
import { Server } from "@leap4/auth";
import { getAllOrgsIdsForUserByServiceName, Mui, MuiIcons } from "@leap4/frontend";
import { BACKEND_SERVICE_NAME, requiredAuthRoles } from "../../config/generals";
import { useAppDispatch, useAppSelector } from "../../store/hooks";

const { Button, Menu, MenuItem, ListItemText, Box, Tooltip } = Mui;
const { Business, Check } = MuiIcons;

// Leap's own action type; the campaign scope and every org-keyed fetch reload when it changes.
const SET_ORGANIZATION = "SET_ORGANIZATION";

interface IOrganizationOption {
    id: string;
    name: string;
}

/** Only for users in several organizations, so a client never sees it. Switches the same way Leap's Navbar does. */
export const OrganizationSwitcher = () => {
    const dispatch = useAppDispatch();
    const user = useAppSelector((state: any) => state.auth?.user);
    const selectedId = useAppSelector((state: any) => state.auth?.selectedOrganizationId) as string | null;
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const options = useMemo<IOrganizationOption[]>(() => {
        if (!user?.organizations) return [];
        return getAllOrgsIdsForUserByServiceName(user, user.organizations, requiredAuthRoles, BACKEND_SERVICE_NAME).map((o: any) => ({
            id: String(o?.id ?? o),
            name: String(o?.name ?? o),
        }));
    }, [user]);

    if (options.length < 2) return null;

    const current = options.find((o) => o.id === selectedId) ?? options[0];
    const choose = (id: string) => {
        setAnchorEl(null);
        if (id === current.id) return;
        localStorage.setItem(Server.headers.X_ORGANZIZATION, id);
        dispatch({ type: SET_ORGANIZATION, payload: id });
    };

    return (
        <>
            <Tooltip title="Organization">
                <Button
                    onClick={(e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
                    startIcon={<Business />}
                    aria-haspopup="listbox"
                    aria-expanded={open || undefined}
                    aria-controls={open ? "organization-menu" : undefined}
                    aria-label={`Organization: ${current.name}. Change organization`}
                    sx={{ color: "text.secondary", fontWeight: 600, minHeight: 40, maxWidth: { xs: 160, sm: 260 } }}
                >
                    <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {current.name}
                    </Box>
                </Button>
            </Tooltip>
            <Menu
                id="organization-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                slotProps={{ paper: { sx: { minWidth: 220, mt: 1 } }, list: { role: "listbox", "aria-label": "Organization" } }}
            >
                {options.map((option) => {
                    const selected = option.id === current.id;
                    return (
                        <MenuItem key={option.id} role="option" aria-selected={selected} selected={selected} onClick={() => choose(option.id)}>
                            <ListItemText primary={option.name} />
                            {selected && <Check fontSize="small" sx={{ ml: 2, color: "primary.main" }} />}
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};
